import { useState, useEffect } from 'react';
import { getGlobalStats } from '../api/client';

interface GlobalStats {
  totalUsers: number;
  totalEvents: number;
  eventsByType: Record<string, number>;
  eventsToday: number;
  eventsThisWeek: number;
}

export default function Stats() {
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchStats() {
      try {
        const result = await getGlobalStats();
        setStats(result.data as GlobalStats);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-gray-500">
        Loading statistics...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-500 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  const eventTypes = [
    { key: 'scan', label: 'Scans', color: 'bg-indigo-500', icon: '&#x1F4F7;' },
    { key: 'app_open', label: 'App Opens', color: 'bg-green-500', icon: '&#x1F4F1;' },
    { key: 'park_visit', label: 'Park Visits', color: 'bg-yellow-500', icon: '&#x1F3F0;' },
    { key: 'store_visit', label: 'Store Visits', color: 'bg-red-500', icon: '&#x1F3EA;' },
    { key: 'pin_submission', label: 'Submissions', color: 'bg-purple-500', icon: '&#x1F4E4;' },
    { key: 'pin_owned', label: 'Pins Owned', color: 'bg-pink-500', icon: '&#x1F4CC;' },
    { key: 'pin_favorited', label: 'Favorited', color: 'bg-teal-500', icon: '&#x2764;' },
    { key: 'availability_check', label: 'Checks', color: 'bg-orange-500', icon: '&#x1F50D;' },
  ];

  return (
    <div>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6 sm:mb-8 pb-5 border-b-2 border-gray-200">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">User Statistics</h2>
          <div className="text-gray-500 text-sm sm:text-base">Analytics and event tracking</div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button className="flex-1 sm:flex-none px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-sm">
            Export CSV
          </button>
          <button className="flex-1 sm:flex-none px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm">
            Refresh
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-6 sm:mb-8">
        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <div className="flex justify-between items-start mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-100 flex items-center justify-center text-lg sm:text-xl">
              &#x1F465;
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">
            {stats?.totalUsers?.toLocaleString() || 0}
          </div>
          <div className="text-gray-500 text-sm">Total Users</div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <div className="flex justify-between items-start mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-purple-100 flex items-center justify-center text-lg sm:text-xl">
              &#x1F4CA;
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">
            {stats?.totalEvents?.toLocaleString() || 0}
          </div>
          <div className="text-gray-500 text-sm">Total Events</div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <div className="flex justify-between items-start mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-green-100 flex items-center justify-center text-lg sm:text-xl">
              &#x1F4C5;
            </div>
            <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-800">Today</span>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">
            {stats?.eventsToday?.toLocaleString() || 0}
          </div>
          <div className="text-gray-500 text-sm">Events Today</div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <div className="flex justify-between items-start mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-orange-100 flex items-center justify-center text-lg sm:text-xl">
              &#x1F4C6;
            </div>
            <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-800">7d</span>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">
            {stats?.eventsThisWeek?.toLocaleString() || 0}
          </div>
          <div className="text-gray-500 text-sm">This Week</div>
        </div>
      </div>

      {/* Events by Type */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4">
          <span className="font-bold text-sm sm:text-base">&#x1F4CA; Events by Type</span>
        </div>
        <div className="p-4 sm:p-5 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {eventTypes.map(({ key, label, color, icon }) => {
            const count = stats?.eventsByType?.[key] || 0;
            const percentage = stats?.totalEvents ? (count / stats.totalEvents) * 100 : 0;

            return (
              <div key={key} className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <span dangerouslySetInnerHTML={{ __html: icon }} />
                  <span className="font-medium text-slate-800 text-sm">{label}</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">
                  {count.toLocaleString()}
                </div>
                <div className="h-1.5 sm:h-2 bg-gray-200 rounded overflow-hidden">
                  <div className={`h-full ${color}`} style={{ width: `${percentage}%` }} />
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {percentage.toFixed(1)}%
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Activity Info */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4">
          <span className="font-bold text-sm sm:text-base">&#x2139;&#xFE0F; About Statistics</span>
        </div>
        <div className="p-4 sm:p-5">
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
            Statistics are collected anonymously to help improve the app experience. Events are tracked
            when users scan pins, visit parks and stores, open the app, and interact with the pin catalog.
            All data is aggregated and no personally identifiable information is stored.
          </p>
        </div>
      </div>
    </div>
  );
}
